/* Bettr Sauna — Observability (Phase 21)
 * --------------------------------------------------------------------
 * Collects Core Web Vitals, Long Tasks with attribution, uncaught
 * errors, unhandled rejections, and User Timing marks. Buffers and
 * sends them via sendBeacon to `window.BETTR_METRICS_ENDPOINT` when
 * set; otherwise logs each record to `console.debug` so the data is
 * visible during local development.
 *
 * No external dependencies. A minimal inline web-vitals-lite that
 * covers LCP, CLS, INP (via `event` entries + `first-input`), TTFB,
 * FCP. Good enough for real-user telemetry without shipping the
 * 3 KB web-vitals library.
 *
 * Public API (window.BettrMetrics):
 *   .mark(name)                        — performance.mark + record
 *   .measure(name, startMark, endMark) — performance.measure + record
 *   .send(force)                       — flush buffer now (also runs
 *                                        on pagehide automatically)
 *
 * Each record shape:
 *   { type, name, value, id, attribution?, ts, page, nav }
 *
 * To activate transmission, set in the theme:
 *   <script>window.BETTR_METRICS_ENDPOINT = 'https://metrics.bettrsauna.com/v1'</script>
 * Endpoint should accept an application/json array and respond 2xx.
 * -------------------------------------------------------------------- */

(function () {
  'use strict';

  const ENDPOINT = () => window.BETTR_METRICS_ENDPOINT || null;
  const PAGE_ID = (crypto && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()) + '-' + Math.random().toString(36).slice(2);
  const NAV_TYPE = (performance && performance.getEntriesByType && performance.getEntriesByType('navigation')[0]?.type) || 'unknown';

  const buffer = [];
  let flushTimer = null;

  function record(type, name, value, extra) {
    const r = {
      type,
      name,
      value,
      ts: Date.now(),
      page: location.pathname,
      nav: NAV_TYPE,
      id: PAGE_ID,
    };
    if (extra) Object.assign(r, extra);
    buffer.push(r);
    if (!ENDPOINT()) {
      try { console.debug('[BettrMetrics]', r); } catch (_) {}
    }
    scheduleFlush();
  }

  function scheduleFlush() {
    if (flushTimer) return;
    flushTimer = setTimeout(flush, 3000);
  }

  function flush() {
    flushTimer = null;
    if (buffer.length === 0) return;
    const endpoint = ENDPOINT();
    if (!endpoint) { buffer.length = 0; return; }
    const payload = buffer.splice(0, buffer.length);
    const body = JSON.stringify(payload);
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(endpoint, blob);
        return;
      }
    } catch (_) { /* fall through to fetch */ }
    try {
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      });
    } catch (_) {}
  }

  window.addEventListener('pagehide', () => flush(), { capture: true });
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });

  // ------------------------------------------------------------------
  // PerformanceObserver helpers.
  // ------------------------------------------------------------------
  function observe(entryTypes, cb, opts) {
    if (typeof PerformanceObserver !== 'function') return null;
    try {
      const po = new PerformanceObserver((list) => list.getEntries().forEach(cb));
      po.observe(Object.assign({ type: entryTypes[0], buffered: true }, opts || {}));
      return po;
    } catch (_) { return null; }
  }

  // ------------------------------------------------------------------
  // Core Web Vitals — minimal inline collectors.
  // ------------------------------------------------------------------
  // TTFB + FCP from the Navigation Timing entry.
  if (performance && performance.getEntriesByType) {
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav) {
      record('webvital', 'TTFB', nav.responseStart - nav.requestStart);
    }
    const paint = performance.getEntriesByType('paint');
    const fcp = paint.find(p => p.name === 'first-contentful-paint');
    if (fcp) record('webvital', 'FCP', fcp.startTime);
  }

  // LCP — take the final value on pagehide; otherwise the latest.
  let lastLCP = null;
  observe(['largest-contentful-paint'], (e) => {
    lastLCP = e;
  });
  window.addEventListener('pagehide', () => {
    if (lastLCP) record('webvital', 'LCP', lastLCP.startTime, {
      attribution: { element: lastLCP.element ? (lastLCP.element.tagName + (lastLCP.element.id ? '#' + lastLCP.element.id : '')) : null, url: lastLCP.url || null },
    });
  }, { once: true, capture: true });

  // CLS — sum all layout-shift entries without a recent user input.
  let cls = 0;
  observe(['layout-shift'], (e) => {
    if (!e.hadRecentInput) cls += e.value;
  });
  window.addEventListener('pagehide', () => record('webvital', 'CLS', cls), { once: true, capture: true });

  // INP — track max event duration for user interactions.
  let worstINP = 0;
  observe(['event'], (e) => {
    if (e.duration > worstINP) worstINP = e.duration;
  }, { durationThreshold: 40 });
  observe(['first-input'], (e) => {
    if (e.duration > worstINP) worstINP = e.duration;
  });
  window.addEventListener('pagehide', () => record('webvital', 'INP', worstINP), { once: true, capture: true });

  // ------------------------------------------------------------------
  // Long Tasks with attribution.
  // ------------------------------------------------------------------
  observe(['longtask'], (e) => {
    const src = (e.attribution && e.attribution[0]) ? e.attribution[0] : {};
    record('longtask', 'longtask', e.duration, {
      attribution: {
        name: src.name,
        containerSrc: src.containerSrc,
        containerType: src.containerType,
      },
    });
  });

  // ------------------------------------------------------------------
  // Uncaught errors + unhandled rejections.
  // ------------------------------------------------------------------
  window.addEventListener('error', (e) => {
    record('error', 'js-error', 1, {
      attribution: {
        message: e.message,
        filename: e.filename,
        line: e.lineno,
        col: e.colno,
        stack: (e.error && e.error.stack) ? String(e.error.stack).slice(0, 2000) : null,
      },
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    record('error', 'unhandled-rejection', 1, {
      attribution: {
        reason: e.reason ? (e.reason.message || String(e.reason)).slice(0, 500) : null,
      },
    });
  });

  // ------------------------------------------------------------------
  // Browser feature detection (P1.5) — one-time snapshot of which
  // progressive-enhancement APIs this session has available. Lets us
  // see what % of real traffic benefits from each Tier 1/2 feature.
  // ------------------------------------------------------------------
  function logFeatureSupport() {
    try {
      const features = {
        viewTransitions: typeof document.startViewTransition === 'function',
        popover: Object.prototype.hasOwnProperty.call(HTMLElement.prototype, 'popover'),
        speculationRules: (typeof HTMLScriptElement !== 'undefined' && typeof HTMLScriptElement.supports === 'function')
          ? HTMLScriptElement.supports('speculationrules') === true
          : false,
        broadcastChannel: typeof BroadcastChannel !== 'undefined',
        webLocks: !!(navigator.locks && typeof navigator.locks.request === 'function'),
        serviceWorker: 'serviceWorker' in navigator,
        indexedDB: 'indexedDB' in window,
        animationTimeline: CSS.supports && CSS.supports('animation-timeline: view()'),
        containerQueries: CSS.supports && CSS.supports('container-type: inline-size'),
        colorMix: CSS.supports && CSS.supports('color: color-mix(in oklch, red, blue)'),
        startingStyle: CSS.supports && CSS.supports('selector(@starting-style)'),
        has: CSS.supports && CSS.supports('selector(html:has(body))'),
        scrollbarGutter: CSS.supports && CSS.supports('scrollbar-gutter: stable'),
        textWrapBalance: CSS.supports && CSS.supports('text-wrap: balance'),
        contentVisibility: CSS.supports && CSS.supports('content-visibility: auto'),
        webShare: typeof navigator.share === 'function',
        vibrate: typeof navigator.vibrate === 'function',
        commandFor: 'commandForElement' in HTMLButtonElement.prototype,
      };
      record('feature-support', 'features', 1, { features });
    } catch (_) { /* detection itself must never throw */ }
  }
  // Fire after first paint so we don't compete with critical work.
  if (document.readyState === 'complete') {
    setTimeout(logFeatureSupport, 0);
  } else {
    window.addEventListener('load', () => setTimeout(logFeatureSupport, 0), { once: true });
  }

  // ------------------------------------------------------------------
  // Public helpers.
  // ------------------------------------------------------------------
  window.BettrMetrics = {
    mark(name) {
      try { performance.mark(name); } catch (_) {}
      record('mark', name, performance.now());
    },
    measure(name, startMark, endMark) {
      try {
        const m = performance.measure(name, startMark, endMark);
        record('measure', name, m.duration);
        return m.duration;
      } catch (_) { return null; }
    },
    send: flush,
  };
})();
